<template>
  <div class="search-area">
    <div class="search-row">
      <div class="search-field">
        <span class="search-icon fas fa-magnifying-glass"></span>
        <input v-model="selfInputs.search.value" class="search-input" type="text" autocomplete="off"
               placeholder="Rechercher…" aria-label="Rechercher dans les lignes affichées" />
        <div class="tooltip-container">
          <span class="tooltip-icon fas fa-circle-info"></span>
          <span class="tooltip-message">Recherche limitée aux lignes affichées. Augmentez le nombre de lignes pour élargir la recherche.</span>
        </div>
      </div>
      <button v-if=elements @click="displayAdvancedSearch" type="button" class="advanced-toggle only-desktop-flex" :class="{'open': isAdvancedSearchActivated}">
        <span class="fas fa-sliders"></span>
        Filtres avancés
        <span v-if="activeCount" class="advanced-count">{{ activeCount }}</span>
      </button>
    </div>
    <div v-if="elements && isAdvancedSearchActivated" class="advanced-panel only-desktop-flex">
      <CustomForm :inputs=selfInputs :submit=buttons.submit>
        <CustomSelectField style="width: 300px" :content=selfInputs.fields />
        <div style="width: 20px"></div>
        <CustomSelectField style="width: 150px" :content=selfInputs.operator />
        <div style="width: 20px"></div>
        <div v-if="!selfInputs.variable && !selfInputs.variableSelect" class="fill-empty"/>
        <CustomTextField v-if="selfInputs.variable" :content=selfInputs.variable class="text-field"/>
        <CustomSelectField v-if="selfInputs.variableSelect" style="width: 150px" :content=selfInputs.variableSelect />
        <div style="width: 20px"></div>
        <CustomButton style="width: 200px" @click="addCurrentFilter" :params=buttons.submit />
      </CustomForm>
    </div>
    <div v-if="isAdvancedSearchActivated && tableId && (CustomTableService.data.get(tableId).currentFilters.length || CustomTableService.data.get(tableId).currentSort.length)" class="chips-container">
      <div v-for="(filter, index) in CustomTableService.data.get(tableId).currentFilters" :key="index" class="chip-container" :title="elements.find(e => e.value === filter.field).label + ' ' + formatOperator(filter.operator) + ' ' + formatVariable(filter)">
        <div class="chip-body">
          <div class="chip-left">
            {{ elements.find(e => e.value === filter.field).label }}
            <span class="chip-operator">{{ formatOperator(filter.operator) }}</span>
            {{ formatVariable(filter) }}
          </div>
          <div class="chip-cross" @click="removeFilter(index)">✕</div>
        </div>
      </div>
      <div v-for="(sort, index) in CustomTableService.data.get(tableId).currentSort" :key="index" class="chip-container sort" :title="elements.find(e => e.value === sort.field).label + ' ' + formatOrder(sort.order)">
        <div class="chip-body">
          <div class="chip-left">
            {{ elements.find(e => e.value === sort.field).label }}
            <span class="chip-operator">{{ formatOrder(sort.order) }}</span>
          </div>
          <div class="chip-cross" @click="removeSort(index)">✕</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { library } from '@fortawesome/fontawesome-svg-core'
  import {faMagnifyingGlass, faMagnifyingGlassPlus, faCircleInfo, faSliders} from "@fortawesome/free-solid-svg-icons";
  import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
  import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
  import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
  import CustomForm from "@/components/forms/CustomForm.vue";
  import {CustomTableService} from "@/services/CustomTableService";
  import {FILTER_OPERATOR_ENUM} from "@/models/filterOperatorEnum";
  import {formattedDateTime} from "@/utils";
  library.add(faMagnifyingGlass);
  library.add(faMagnifyingGlassPlus);
  library.add(faCircleInfo);
  library.add(faSliders);

  export default {
    name: 'SearchArray',
    computed: {
      CustomTableService() {
        return CustomTableService
      },
      // Filtres et tris actifs : affichés en pastille sur le bouton « Filtres avancés »
      activeCount() {
        const table = this.tableId ? CustomTableService.data.get(this.tableId) : undefined;
        return table ? table.currentFilters.length + table.currentSort.length : 0;
      }
    },
    components: {CustomForm, CustomButton, CustomSelectField, CustomTextField},
    props: ['elements', 'ct'],
    inject: ["tableId"],
    data: function() {
      return {
        buttons:{
          submit: {text: "Rechercher"},
        },
        selfInputs: {
          fields: {value: '', type: 'select', selectValues: []},
          search: {label: 'Filtre', value: '', type: 'text', readOnly: false},
          operator: {value: FILTER_OPERATOR_ENUM.GREATER_THAN.value, type: 'select', selectValues: [FILTER_OPERATOR_ENUM.EQUAL, FILTER_OPERATOR_ENUM.GREATER_THAN, FILTER_OPERATOR_ENUM.MINUS_THAN, FILTER_OPERATOR_ENUM.IN, FILTER_OPERATOR_ENUM.NOT_IN], readOnly: true},
          variable: undefined,
          variableSelect: undefined,
        },
        isAdvancedSearchActivated: false,
      }
    },
    watch: {
      "selfInputs.fields.value"(el) {
        const element = this.elements.find(e => e.value === el);

        if (!element) return;

        if (element.type !== 'select') {
          this.selfInputs.variableSelect = undefined;
          this.selfInputs.variable = { label: 'Valeur',  value: '',  type: element.type };
        } else {
          this.selfInputs.variable = undefined;
          const normalizedValues = Array.isArray(element.values) ? element.values : Object.values(element.values || {});
          this.selfInputs.variableSelect = { value: undefined,  type: element.type,  selectValues: normalizedValues,  required: true };
        }

        this.selfInputs.operator.readOnly = false;
      }
    },
    mounted() {
      this.fillFieldsFromElements();
    },
    methods: {
      fillFieldsFromElements(){
        if(this.elements) {
          // sortOnly : colonne calculée par le back, triable mais pas filtrable
          this.selfInputs.fields.selectValues.push(...this.elements.filter(e => e.value && !e.sortOnly));
        }
      },
      formatOperator(op){
        const map = { EQUAL: "=", GREATER_THAN: ">", MINUS_THAN: "<", CONTAIN: "∼", IN: "∈", NOT_IN: "∉" };
        return map[op] || op;
      },
      formatOrder(order){
        const map = { DESCENDING: "descendant", ASCENDING: "ascendant" };
        return map[order] || order;
      },
      formatVariable(filter) {
        const element = this.elements.find(e => e.value === filter.field);

        if (element && element.type === 'select') {
          const values = Array.isArray(element.values) ? element.values : Object.values(element.values || {});
          const variables = Array.isArray(filter.variable) ? filter.variable : [filter.variable];
          const labels = variables
              .map(v => values.find(item => item.value === v)?.label)
              .filter(Boolean);

          return labels.length ? labels.join(', ') : '-';
        } else if (element && element.type === 'date'){
          return (new Date(filter.variable)).toLocaleDateString("en-GB", formattedDateTime);
        }else{
          return filter.variable;
        }
      },
      removeFilter(index){
        CustomTableService.data.get(this.tableId).currentFilters.splice(index, 1);
        CustomTableService.data.get(this.tableId).loadData();
      },
      removeSort(index){
        CustomTableService.data.get(this.tableId).currentSort.splice(index, 1);
        CustomTableService.data.get(this.tableId).loadData();
      },
      displayAdvancedSearch(){
        this.isAdvancedSearchActivated = !this.isAdvancedSearchActivated;
      },
      // Warn ! If some changes are done in data from template for diplay purposes,
      // we are filtering on initial value here !
      simpleFilter(list) {
        const normalize = (str) =>
            str
                .toString()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase();

        const searchValue = this.selfInputs.search.value;

        if (!searchValue) {
          return list;
        }

        const normalizedSearch = normalize(searchValue);

        const containsSearch = (value) => {
          if (value === null || value === undefined) {
            return false;
          }

          // string / number / boolean
          if (typeof value !== "object") {
            return normalize(value).includes(normalizedSearch);
          }

          // tableau
          if (Array.isArray(value)) {
            return value.some(v => containsSearch(v));
          }

          // objet
          return Object.values(value).some(v => containsSearch(v));
        };

        return list.filter(obj => containsSearch(obj));
      },
      addCurrentFilter() {
        const field = this.selfInputs.fields.value;
        const operator = this.selfInputs.operator.value;
        const rawValue = this.selfInputs.variable ? this.selfInputs.variable.value : this.selfInputs.variableSelect?.value;

        if (field && rawValue) {
          const variable = (operator === 'IN' || operator === 'NOT_IN') ? (Array.isArray(rawValue) ? rawValue : [rawValue]) : rawValue;
          CustomTableService.data.get(this.tableId).currentFilters.push({ field,  operator,  variable });
          CustomTableService.data.get(this.tableId).loadData();
        }
      },
      initParams(filters, sorts){
        if(this.tableId) {
          if(filters) {
            CustomTableService.data.get(this.tableId).currentFilters.push(...filters);
          }
          if(sorts){
            CustomTableService.data.get(this.tableId).currentSort.push(...sorts);
          }
        }
      },
      extractFiltersFromURL() {
        const query = this.$route.query;

        let filters = [];
        if (query?.filters) {
          const parsed = JSON.parse(query.filters);
          if (Array.isArray(parsed)) {
            filters = parsed;
          }
        }

        return filters;
      },
      extractSortsFromURL() {
        const query = this.$route.query;

        let sorts = [];
        if (query?.sort) {
          const parsed = JSON.parse(query.sort);
          if (Array.isArray(parsed)) {
            sorts = parsed;
          }
        }

        return sorts;
      },
      /**
       * Init component data with those given in URL
       */
      initWithURLParams(){
        this.initParams(this.extractFiltersFromURL(), this.extractSortsFromURL());
      },
      /**
       * Build component data for URL format
       * @returns {string}
       */
      urlParamsBuilder(){
        let paramsBuilder = "";

        if(this.tableId && CustomTableService.data.get(this.tableId).currentFilters.length){
          const encodedFilters = encodeURIComponent(JSON.stringify(CustomTableService.data.get(this.tableId).currentFilters));
          paramsBuilder += `filters=${encodedFilters}&`;
        }

        if(this.tableId && CustomTableService.data.get(this.tableId).currentSort.length){
          const encodedSort = encodeURIComponent(JSON.stringify(CustomTableService.data.get(this.tableId).currentSort));
          paramsBuilder += `sort=${encodedSort}&`;
        }

        return paramsBuilder;
      }
    }
  }
</script>

<style lang="stylus" scoped>
  @require '~/src/common/style/common.styl';

  // Barre d'outils : interrupteur, recherche, « Filtres avancés » (juste à droite de la recherche), action à droite.
  // Une fois « Filtres avancés » ouvert : ligne des pastilles (filtres/tris actifs), puis formulaire d'ajout de filtre.
  // Les éléments de ce composant se placent directement dans la barre d'outils (CustomTable), qui gère les retours à la ligne.
  .search-area, .search-row
    display contents

  // Champ de recherche toujours visible : loupe à gauche, infobulle à droite
  .search-field
    position relative
    flex 1 1 340px
    max-width 520px
    min-width 220px

  .search-icon
    position absolute
    left 13px
    top 13px
    color CTRL_TEXT_MUTED
    pointer-events none

  .search-input
    box-sizing border-box
    width 100%
    height CTRL_HEIGHT
    padding 0 40px 0 38px
    border 1px solid CTRL_BORDER
    border-radius CTRL_RADIUS
    background-color CTRL_BG
    color CTRL_TEXT
    font-family inherit
    font-size 14px
    font-weight 600
    outline none
    transition background-color CTRL_EASE, border-color CTRL_EASE, box-shadow CTRL_EASE
    &::placeholder
      color CTRL_TEXT_MUTED
      font-weight 400
    &:hover
      background-color CTRL_BG_HOVER
      border-color CTRL_BORDER_HOVER
    &:focus
      background-color CTRL_BG_FOCUS
      border-color CTRL_FOCUS
      box-shadow 0 0 0 3px CTRL_FOCUS_RING

  .tooltip-container
    position absolute
    right 11px
    top 0
    height CTRL_HEIGHT
    display flex
    align-items center

  .tooltip-icon
    color INFO
    cursor help
    &:hover + .tooltip-message
      visibility visible
      opacity 1

  .tooltip-message
    position absolute
    top 100%
    right -8px
    z-index 10
    width 18em
    margin-top 4px
    padding 8px 10px
    border-radius 8px
    background-color #17233f
    color white
    font-size 12px
    font-weight 600
    visibility hidden
    opacity 0
    transition visibility 0s, opacity .2s linear

  .tooltip-message:hover
    visibility visible
    opacity 1

  // Bouton « Filtres avancés » (pastille citron = nombre de filtres et tris actifs)
  .advanced-toggle
    order 1
    align-items center
    gap 8px
    height CTRL_HEIGHT
    padding 0 14px
    border 1px solid CTRL_BORDER
    border-radius CTRL_RADIUS
    background-color white
    color THEME_COLOR_PRIMARY
    font-family inherit
    font-size 14px
    font-weight 700
    cursor pointer
    transition background-color CTRL_EASE, border-color CTRL_EASE, color CTRL_EASE
    &:hover
      background-color CTRL_BG
      border-color CTRL_BORDER_HOVER
    &.open
      background-color THEME_COLOR_PRIMARY
      border-color THEME_COLOR_PRIMARY
      color white
    .advanced-count
      display inline-flex
      align-items center
      justify-content center
      min-width 20px
      height 20px
      padding 0 6px
      border-radius 10px
      background-color PAGE_LEMON
      color FONT_COLOR
      font-size 12px

  // Panneau de recherche avancée : Champ / Opérateur / Valeur / Rechercher
  .advanced-panel
    order 30
    flex-basis 100%
    box-sizing border-box
    padding 12px 14px
    border 1px solid PAGE_RULE_SOFT
    border-radius 12px
    background-color #FAFBF7

    // Le libellé « Valeur » est au-dessus du champ : on aligne les bas des champs et du bouton
    form
      flex-direction row
      align-items flex-end

    .text-field
      flex 1 1 auto
      min-width 120px

  .fill-empty
    flex 1
    height CTRL_HEIGHT
    border-radius CTRL_RADIUS
    background-color CTRL_DISABLED_BG

  // Pastilles des filtres et tris actifs
  // Ligne dédiée, entre la barre d'outils et le formulaire d'ajout de filtre
  .chips-container
    order 21
    flex-basis 100%
    display flex
    flex-wrap wrap
    gap 8px

    .chip-container
      display flex
      align-items center
      min-width 0
      // Un filtre à rallonge (longue liste de valeurs) est tronqué par « … » : l'infobulle donne son texte entier
      max-width unquote("min(100%, 380px)")
      height 30px
      padding 0 6px 0 12px
      border 1px solid CTRL_BORDER
      border-radius 15px
      background-color CTRL_BG
      color FONT_COLOR
      font-size 13px
      font-weight 600
      white-space nowrap
      &.sort
        background-color white
        border-style dashed

      .chip-body
        min-width 0
        display flex
        align-items center
        justify-content space-between
        gap 8px

        .chip-left
          min-width 0
          overflow hidden
          text-overflow ellipsis
          white-space nowrap

          .chip-operator
            margin-right 3px
            margin-left 3px
            color CTRL_TEXT_MUTED
            font-weight 700

        .chip-cross
          flex-shrink 0
          display grid
          place-items center
          width 20px
          height 20px
          border-radius 50%
          color CTRL_TEXT_MUTED
          font-size 12px
          cursor pointer
          &:hover
            background-color CTRL_BORDER
            color FONT_COLOR
</style>
